import React from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PER_PAGE = 3;
const MAX_VISIBLE_PAGES = 5;

const Pagination = ({ pagination, searchParams }) => {
  const [, setSearchParams] = useSearchParams();

  const totalPages = pagination?.total_pages || 1;
  const currentPage = pagination?.page || 1;
  const perPage = pagination?.per_page || DEFAULT_PER_PAGE;
  const totalItems = pagination?.total || 0;
  const hasPrev = typeof pagination?.has_prev === 'boolean' ? pagination.has_prev : currentPage > 1;
  const hasNext = typeof pagination?.has_next === 'boolean' ? pagination.has_next : currentPage < totalPages;

  const goToPage = (targetPage) => {
    const safePage = Math.max(1, Math.min(targetPage, totalPages));
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', safePage);
    newParams.set('per_page', perPage);
    setSearchParams(newParams);
  };

  const generatePageNumbers = () => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    let start = Math.max(1, currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
    let end = start + MAX_VISIBLE_PAGES - 1;

    if (end > totalPages) {
      end = totalPages;
      start = totalPages - MAX_VISIBLE_PAGES + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const pageNumbers = generatePageNumbers();
  const showFirst = pageNumbers[0] > 1;
  const showLast = pageNumbers[pageNumbers.length - 1] < totalPages;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(currentPage * perPage, totalItems);

  return (
    <div className="pagination">
      <div className="pagination-controls" role="navigation" aria-label="Pagination">
        <button
          type="button"
          className="pagination-btn"
          onClick={() => goToPage(currentPage - 1)}
          disabled={!hasPrev}
          aria-label="Go to previous page"
        >
          <i className="bi bi-chevron-left"></i>
          <span className="d-none d-sm-inline">Previous</span>
        </button>

        <div className="pagination-pages">
          {showFirst && (
            <>
              <button
                type="button"
                className={`pagination-btn pagination-number ${currentPage === 1 ? 'active' : ''}`}
                onClick={() => goToPage(1)}
                aria-label="Go to first page"
                aria-current={currentPage === 1 ? 'page' : undefined}
              >
                1
              </button>
              <span className="pagination-ellipsis" aria-hidden="true">…</span>
            </>
          )}

          {pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={`pagination-btn pagination-number ${pageNumber === currentPage ? 'active' : ''}`}
              onClick={() => goToPage(pageNumber)}
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              aria-label={`Go to page ${pageNumber}`}
            >
              {pageNumber}
            </button>
          ))}

          {showLast && (
            <>
              <span className="pagination-ellipsis" aria-hidden="true">…</span>
              <button
                type="button"
                className={`pagination-btn pagination-number ${currentPage === totalPages ? 'active' : ''}`}
                onClick={() => goToPage(totalPages)}
                aria-label="Go to last page"
                aria-current={currentPage === totalPages ? 'page' : undefined}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          className="pagination-btn"
          onClick={() => goToPage(currentPage + 1)}
          disabled={!hasNext}
          aria-label="Go to next page"
        >
          <span className="d-none d-sm-inline">Next</span>
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>

      <div className="pagination-info" aria-live="polite">
        {totalItems > 0
          ? `Showing ${startItem}–${endItem} of ${totalItems} ${totalItems === 1 ? 'company' : 'companies'}`
          : 'No companies found'}
      </div>
    </div>
  );
};

export default Pagination;
